import { advisoryService, brokersService, reportsService } from '@/services';
import type { ReportFilterOptions } from '@/services';
import { useQuery } from '@tanstack/react-query';

export interface FilterOption {
  label: string;
  value: string;
}

const OPTIONS_PAGE_SIZE = 100;

const EMPTY_FILTER_OPTIONS: ReportFilterOptions = {
  products: [],
  branches: [],
  policyStatuses: [],
  installmentStatuses: [],
  periodTypes: [],
};

/**
 * Opções servidas pelo backend: Produto (nr_produto), Filial (cd_filial), Status
 * da apólice/parcela e Tipo de Período. Cacheado (mudam com pouca frequência).
 */
export function useReportsFilterOptions() {
  return useQuery({
    queryKey: ['reports', 'filter-options'],
    staleTime: 1000 * 60 * 30,
    queryFn: async (): Promise<ReportFilterOptions> => {
      const response = await reportsService.getFilterOptions();
      return response.success && response.data ? response.data : EMPTY_FILTER_OPTIONS;
    },
  });
}

/**
 * Opções do dropdown de Assessoria (só faz sentido para o perfil seguradora).
 * Valor = PartyCode (o backend resolve para o id do i4pro).
 */
export function useAdvisoryOptions(enabled: boolean) {
  return useQuery({
    queryKey: ['reports', 'advisory-options'],
    enabled,
    staleTime: 1000 * 60 * 10,
    queryFn: async (): Promise<FilterOption[]> => {
      const response = await advisoryService.getAdvisories({ pageSize: OPTIONS_PAGE_SIZE });
      if (!response.success || !response.data) return [];
      return response.data.data
        .filter(advisory => advisory.partyCode)
        .map(advisory => ({ label: advisory.name || advisory.partyName, value: advisory.partyCode }));
    },
  });
}

/**
 * Opções do dropdown de Corretor (seguradora e assessoria). Valor = PartyCode.
 */
export function useBrokerOptions(enabled: boolean) {
  return useQuery({
    queryKey: ['reports', 'broker-options'],
    enabled,
    staleTime: 1000 * 60 * 10,
    queryFn: async (): Promise<FilterOption[]> => {
      const response = await brokersService.listBrokers({ pageSize: OPTIONS_PAGE_SIZE });
      if (!response.success || !response.data) return [];
      // O tipo de retorno do serviço tem aninhamento inconsistente; navega defensivo.
      const raw = response.data as unknown as { data?: unknown };
      const list = (
        Array.isArray(raw.data) ? raw.data : ((raw.data as { data?: unknown })?.data ?? [])
      ) as Array<{ code?: string; name?: string }>;
      return list
        .filter(broker => broker.code)
        .map(broker => ({ label: broker.name ?? '', value: broker.code as string }));
    },
  });
}
